import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchPartnerDetails, getAllPartnerTeams } from "../../services/PartnerService";
import PartnerUsersSection from "./PartnerUsersSection";
import useGuidedTour from "../../hooks/useGuidedTour";
import {
  USERS_OVERVIEW_TOUR_ID,
  USERS_ADD_USER_TOUR_ID,
  buildUsersOverviewTourSteps,
  buildUsersAddUserTourSteps,
} from "./Users.tour";
import { canManagePartnerDetails } from "../../utils/auth/accessRules";
import { PERMISSION_SLUGS } from "../../utils/auth/permissionConstants";

const Users = () => {
  const authUser = useSelector((state) => state.auth.authUser);
  const partnerId = authUser?.partner_id;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const teamIdFromUrl = searchParams.get("team") || null;
  const tourParam = searchParams.get("tour");

  const { data: partnerDetailsData } = useQuery({
    queryKey: ["partnerDetails", partnerId],
    queryFn: () => fetchPartnerDetails(partnerId),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });

  const userPartnerDetails = partnerDetailsData?.data?.data ?? null;

  const canManageUsers = canManagePartnerDetails(
    authUser,
    userPartnerDetails,
    PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  );

  const addUserTourEnabled = tourParam === "add-user" && canManageUsers;

  useGuidedTour(
    USERS_OVERVIEW_TOUR_ID,
    buildUsersOverviewTourSteps(),
    { enabled: !!authUser?.id && !addUserTourEnabled },
  );

  useGuidedTour(
    USERS_ADD_USER_TOUR_ID,
    buildUsersAddUserTourSteps(),
    { enabled: addUserTourEnabled, startDelayMs: 500 },
  );

  // When the FAQ replays the Add User walkthrough it navigates to
  // /users?tour=add-user. Auto-open the Add User modal so every step in the
  // tour (which targets fields inside the modal) has its element on screen
  // by the time the guided tour polls for them. Gated by the same permission
  // that renders the button, so a user without MANAGE_PARTNER_USERS will not
  // trigger a click that would silently fail against a missing button.
  useEffect(() => {
    if (!addUserTourEnabled) return;
    let attempts = 0;
    const tryClick = () => {
      const buttons = document.querySelectorAll('[data-tour="users-add-button"]');
      for (const btn of buttons) {
        if (btn.offsetParent !== null) {
          btn.click();
          return;
        }
      }
      attempts += 1;
      if (attempts < 20) window.setTimeout(tryClick, 100);
    };
    tryClick();
  }, [addUserTourEnabled]);

  // Once the Add User walkthrough completes, drop the ?tour=add-user query
  // so the URL a user reads, bookmarks, or shares does not still say "run
  // the tour". Only fires when the tour was actually requested via the FAQ
  // replay flow, and the completion id has landed on the user record.
  const addUserTourCompleted = (authUser?.completed_tour_ids ?? []).includes(USERS_ADD_USER_TOUR_ID);
  useEffect(() => {
    if (tourParam !== "add-user") return;
    if (!addUserTourCompleted) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tour");
    setSearchParams(next, { replace: true });
  }, [tourParam, addUserTourCompleted, searchParams, setSearchParams]);

  const { data: allTeamsData } = useQuery({
    queryKey: ["allPartnerTeams", partnerId],
    queryFn: () => getAllPartnerTeams(partnerId),
    enabled: !!partnerId && !!teamIdFromUrl,
    staleTime: 5 * 60 * 1000,
  });
  const allTeams = allTeamsData?.data?.data ?? null;

  const teamFilter = useMemo(() => {
    if (!teamIdFromUrl) return null;
    const match = allTeams?.find((t) => String(t.id) === String(teamIdFromUrl));
    if (!match) return { id: teamIdFromUrl, name: "Selected team" };
    return { id: match.id, name: match.name };
  }, [teamIdFromUrl, allTeams]);

  const clearTeamFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="row">
      <div className="col-12">
        <PartnerUsersSection
          partnerId={partnerId}
          authUser={authUser}
          userPartnerDetails={userPartnerDetails}
          teamFilter={teamFilter}
          onClearTeamFilter={teamFilter ? clearTeamFilter : undefined}
          onAddExpenseTag={() => navigate("/admin-details#expense-tags")}
          onAddTeam={() => navigate("/teams")}
        />
      </div>
    </div>
  );
};

export default Users;
